import { NavLink, Outlet } from 'react-router-dom';
import clsx from 'clsx';
import {
  Squares2X2Icon,
  ShieldCheckIcon,
  ChatBubbleLeftRightIcon,
  BellAlertIcon,
  SparklesIcon,
  KeyIcon,
  UsersIcon,
  BoltIcon,
  WrenchScrewdriverIcon,
  ClipboardDocumentCheckIcon,
  UserGroupIcon,
  MapPinIcon,
  MegaphoneIcon,
  QueueListIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { navVisible } from '../../config/navVisibility';
import { PERMISSIONS } from '../../config/rolePermissions';
import { TOOL_KEYS } from '../../config/toolAccess';

const LINKS = [
  { to: '/settings', label: 'Overview', end: true, icon: Squares2X2Icon, permission: PERMISSIONS.MANAGE_SETTINGS },
  { to: '/settings/people', label: 'People', icon: UsersIcon, permission: PERMISSIONS.VIEW_USERS },
  { to: '/settings/client-team-map', label: 'Client → Team Mapping', icon: MapPinIcon, permission: [PERMISSIONS.VIEW_USERS, PERMISSIONS.MANAGE_TEAMS] },
  { to: '/settings/campaign-managers', label: 'Campaign Managers', icon: MegaphoneIcon, permission: [PERMISSIONS.MANAGE_ACCESS_CONTROL, PERMISSIONS.MANAGE_CLIENTS] },
  { to: '/settings/leads-teams', label: 'Leads Teams', icon: UserGroupIcon, toolKey: TOOL_KEYS.LEADS_TRACKER },
  { to: '/settings/security', label: 'Security', icon: ShieldCheckIcon },
  { to: '/settings/notifications', label: 'Notifications', icon: ChatBubbleLeftRightIcon, permission: PERMISSIONS.MANAGE_SETTINGS },
  { to: '/settings/notification-rules', label: 'Notification Rules', icon: BellAlertIcon, permission: PERMISSIONS.MANAGE_SETTINGS },
  { to: '/settings/noti-queue', label: 'Noti Queue', icon: QueueListIcon, adminOnly: true },
  { to: '/settings/prompt-templates', label: 'Prompt Templates', icon: SparklesIcon, permission: PERMISSIONS.MANAGE_SETTINGS },
  { to: '/settings/auto-generation', label: 'Automated Generation', icon: BoltIcon, permission: PERMISSIONS.MANAGE_AUTO_GENERATION },
  { to: '/settings/role-access', label: 'Role Access', icon: ShieldCheckIcon, permission: PERMISSIONS.MANAGE_ACCESS_CONTROL },
  { to: '/settings/escalation', label: 'Escalation', icon: BoltIcon, permission: PERMISSIONS.VIEW_ESCALATION },
  { to: '/settings/tool-access', label: 'Tools In Use', icon: WrenchScrewdriverIcon, permission: PERMISSIONS.MANAGE_ACCESS_CONTROL },
  { to: '/settings/task-verification', label: 'Task Verification', icon: ShieldCheckIcon, permission: PERMISSIONS.MANAGE_ACCESS_CONTROL },
  // The mapping that decides which service switch gates which tool, for every
  // location at once. It existed only inside the Tool composer, one tool at a time.
  { to: '/settings/services-tools', label: 'Services & Tools', icon: Squares2X2Icon, permission: PERMISSIONS.MANAGE_SETTINGS },
  { to: '/settings/api-keys', label: 'API Keys', icon: KeyIcon, permission: PERMISSIONS.MANAGE_API_KEYS },
  { to: '/settings/task-routing', label: 'Task', icon: ClipboardDocumentCheckIcon, permission: PERMISSIONS.MANAGE_SETTINGS },
  /* The agency-wide QC gate: review cap, age-out, action tasks and their
     cutoff, and the client email. Per-client switches live on the client. */
  { to: '/settings/quality-control', label: 'Quality Control', icon: ShieldCheckIcon, permission: PERMISSIONS.MANAGE_SETTINGS },
  /**
   * LAST — where deleted tasks go, and how they come back.
   *
   * `permission`, not `adminOnly`: `DELETE_TASKS` is grantable from Role Access,
   * and whoever may delete a task must be able to undo it. Hard-coding the role
   * here would mean granting the permission produced a delete button with no way
   * back.
   */
  { to: '/settings/recycle-bin', label: 'Recycle Bin', icon: TrashIcon, permission: PERMISSIONS.DELETE_TASKS },
];

export default function SettingsLayout() {
  const { user } = useAuth();

  const links = LINKS.filter((l) => navVisible(l, user));

  return (
    <div className="flex flex-col lg:flex-row gap-5 lg:gap-6">
      <aside className="lg:w-60 shrink-0 sticky top-0 z-30 self-start bg-surface-50/95 backdrop-blur-sm pb-2">
        <nav className="panel-card p-2 overflow-hidden ring-1 ring-brand-500/10">
          <div className="h-1 -mx-2 -mt-2 mb-2 bg-gradient-to-r from-brand-400 via-brand-500 to-brand-700" aria-hidden />
          <p className="px-2.5 pt-1 pb-1 text-[12px] font-semibold uppercase tracking-widest text-brand-600">
            Settings
          </p>
          <div className="flex lg:flex-col gap-0.5 overflow-x-auto lg:overflow-visible pb-1 lg:pb-2">
            {links.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.end}
                className={({ isActive }) =>
                  clsx(
                    'flex items-center gap-2.5 whitespace-nowrap px-2.5 py-2 rounded-lg text-xs font-medium transition-colors',
                    isActive
                      ? 'bg-brand-500/10 text-brand-700 border border-brand-500/25 shadow-sm shadow-brand-500/5'
                      : 'text-surface-600 hover:bg-brand-50/60 hover:text-brand-800 border border-transparent',
                  )
                }
              >
                <link.icon className="h-4 w-4 shrink-0 opacity-90" />
                {link.label}
              </NavLink>
            ))}
          </div>
        </nav>
      </aside>
      <div className="flex-1 min-w-0">
        <Outlet />
      </div>
    </div>
  );
}
